// ============================================================================
// PROJECT STAGE STATUS & ENTRIES - SUPABASE HOOKS
// ============================================================================
// Partial invoicing & partial closure tracking per project × stage.
// Option A: Percent-of-stage based.
//
// Two tables:
//   project_stage_status  — rolled-up totals (analytics reads THIS)
//   project_stage_entries — ledger of partial entries (UI breakdown)
//
// All aggregation logic lives here in the app (no DB triggers).
// ============================================================================

import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError } from './useSupabase';

// ============================================================================
// SESSION GUARD (mirrors useSupabase.js pattern)
// ============================================================================
const requireSession = async () => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw new Error('Authentication error: ' + sessionError.message);
  if (!session || !session.user) {
    throw new Error(
      'Please sign in to perform this action. ' +
      'Note: The "switch user" feature only changes the UI view — ' +
      'it does not change your database permissions.'
    );
  }
  return session.user.id;
};

// ============================================================================
// SQL MIGRATION (run in Supabase SQL Editor)
// ============================================================================
//
// -- 1. Rolled-up totals — the single source of truth for analytics
// CREATE TABLE IF NOT EXISTS project_stage_status (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   project_id bigint NOT NULL REFERENCES projects(id) ON DELETE CASCADE,
//   stage_id integer NOT NULL,
//   closed_percent integer NOT NULL DEFAULT 0
//     CHECK (closed_percent BETWEEN 0 AND 100),
//   invoiced_percent integer NOT NULL DEFAULT 0
//     CHECK (invoiced_percent BETWEEN 0 AND 100),
//   updated_at timestamptz DEFAULT now(),
//   UNIQUE (project_id, stage_id)
// );
//
// -- 2. Ledger of partial entries — audit trail for UI
// CREATE TABLE IF NOT EXISTS project_stage_entries (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   project_id bigint NOT NULL REFERENCES projects(id) ON DELETE CASCADE,
//   stage_id integer NOT NULL,
//   type text NOT NULL CHECK (type IN ('invoice', 'closure')),
//   percent integer NOT NULL CHECK (percent BETWEEN 1 AND 100),
//   entry_date date NOT NULL,
//   note text,
//   created_at timestamptz DEFAULT now()
// );
//
// CREATE INDEX IF NOT EXISTS idx_pse_project_stage
//   ON project_stage_entries (project_id, stage_id);
// CREATE INDEX IF NOT EXISTS idx_pse_project_stage_type
//   ON project_stage_entries (project_id, stage_id, type);
//
// -- RLS policies (adjust to match your existing pattern)
// ALTER TABLE project_stage_status ENABLE ROW LEVEL SECURITY;
// ALTER TABLE project_stage_entries ENABLE ROW LEVEL SECURITY;
//
// -- Read: all authenticated users
// CREATE POLICY "select_project_stage_status" ON project_stage_status
//   FOR SELECT TO authenticated USING (true);
// CREATE POLICY "select_project_stage_entries" ON project_stage_entries
//   FOR SELECT TO authenticated USING (true);
//
// -- Write: all authenticated users (app enforces role checks)
// CREATE POLICY "insert_project_stage_status" ON project_stage_status
//   FOR INSERT TO authenticated WITH CHECK (true);
// CREATE POLICY "update_project_stage_status" ON project_stage_status
//   FOR UPDATE TO authenticated USING (true);
// CREATE POLICY "insert_project_stage_entries" ON project_stage_entries
//   FOR INSERT TO authenticated WITH CHECK (true);
// CREATE POLICY "delete_project_stage_entries" ON project_stage_entries
//   FOR DELETE TO authenticated USING (true);
//
// ============================================================================

// ============================================================================
// HOOK: useProjectStageStatus
// Manages rolled-up status (closed_percent, invoiced_percent) per stage.
// Analytics should read from this cache only.
// ============================================================================
export const useProjectStageStatus = () => {
  // Cache: { [projectId]: { [stageId]: { closedPercent, invoicedPercent } } }
  const [statusCache, setStatusCache] = useState({});
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------------------
  // Fetch all stage statuses for a single project
  // Returns: { [stageId]: { closedPercent, invoicedPercent } }
  // ------------------------------------------------------------------
  const fetchProjectStageStatus = useCallback(async (projectId) => {
    if (!projectId) return {};
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('project_stage_status')
        .select('*')
        .eq('project_id', projectId);

      if (error) throw error;

      const mapped = {};
      (data || []).forEach(row => {
        mapped[row.stage_id] = {
          closedPercent: row.closed_percent ?? 0,
          invoicedPercent: row.invoiced_percent ?? 0,
        };
      });

      // Update cache for this project
      setStatusCache(prev => ({ ...prev, [projectId]: mapped }));
      return mapped;
    } catch (err) {
      console.error('[PROJECT_STAGE_STATUS] Fetch error:', err);
      return {};
    } finally {
      setLoading(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // Fetch statuses for multiple projects at once (batch load for analytics)
  // Returns: { [projectId]: { [stageId]: { closedPercent, invoicedPercent } } }
  // ------------------------------------------------------------------
  const fetchAllProjectStageStatuses = useCallback(async (projectIds) => {
    if (!projectIds || projectIds.length === 0) return {};
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('project_stage_status')
        .select('*')
        .in('project_id', projectIds);

      if (error) throw error;

      const result = {};
      (data || []).forEach(row => {
        if (!result[row.project_id]) result[row.project_id] = {};
        result[row.project_id][row.stage_id] = {
          closedPercent: row.closed_percent ?? 0,
          invoicedPercent: row.invoiced_percent ?? 0,
        };
      });

      setStatusCache(prev => ({ ...prev, ...result }));
      return result;
    } catch (err) {
      console.error('[PROJECT_STAGE_STATUS] Batch fetch error:', err);
      return {};
    } finally {
      setLoading(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // Get status from cache (synchronous, no fetch)
  // ------------------------------------------------------------------
  const getStatusFromCache = useCallback((projectId, stageId) => {
    const projectStatuses = statusCache[projectId];
    if (!projectStatuses) return { closedPercent: 0, invoicedPercent: 0 };
    return projectStatuses[stageId] || { closedPercent: 0, invoicedPercent: 0 };
  }, [statusCache]);

  // ------------------------------------------------------------------
  // Upsert status row (called after entry add/delete)
  // ------------------------------------------------------------------
  const upsertStatus = useCallback(async (projectId, stageId, closedPercent, invoicedPercent) => {
    try {
      await requireSession();

      const { error } = await supabase
        .from('project_stage_status')
        .upsert({
          project_id: projectId,
          stage_id: stageId,
          closed_percent: closedPercent,
          invoiced_percent: invoicedPercent,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'project_id,stage_id',
        });

      if (error) throw error;

      // Update local cache immediately (optimistic)
      setStatusCache(prev => ({
        ...prev,
        [projectId]: {
          ...(prev[projectId] || {}),
          [stageId]: { closedPercent, invoicedPercent },
        },
      }));

      return { success: true };
    } catch (err) {
      console.error('[PROJECT_STAGE_STATUS] Upsert error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'stage status' }) };
    }
  }, []);

  return {
    statusCache,
    loading,
    fetchProjectStageStatus,
    fetchAllProjectStageStatuses,
    getStatusFromCache,
    upsertStatus,
  };
};


// ============================================================================
// HOOK: useProjectStageEntries
// Manages the ledger of partial invoice/closure entries.
// Each entry has: type, percent, entry_date, note
// After any mutation, recalculates totals → upserts into project_stage_status.
// ============================================================================
export const useProjectStageEntries = (statusHook) => {
  // Entries for the currently-expanded stage: [{ id, type, percent, entryDate, note, createdAt }]
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);

  // Track which project+stage is loaded
  const [loadedKey, setLoadedKey] = useState(null); // "projectId-stageId"

  // ------------------------------------------------------------------
  // Fetch ledger entries for a specific project × stage
  // ------------------------------------------------------------------
  const fetchEntries = useCallback(async (projectId, stageId) => {
    const key = `${projectId}-${stageId}`;
    setLoadingEntries(true);
    try {
      const { data, error } = await supabase
        .from('project_stage_entries')
        .select('*')
        .eq('project_id', projectId)
        .eq('stage_id', stageId)
        .order('entry_date', { ascending: true });

      if (error) throw error;

      const mapped = (data || []).map(e => ({
        id: e.id,
        projectId: e.project_id,
        stageId: e.stage_id,
        type: e.type,
        percent: e.percent,
        entryDate: e.entry_date,
        note: e.note || '',
        createdAt: e.created_at,
      }));

      setEntries(mapped);
      setLoadedKey(key);
      return mapped;
    } catch (err) {
      console.error('[PROJECT_STAGE_ENTRIES] Fetch error:', err);
      setEntries([]);
      return [];
    } finally {
      setLoadingEntries(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // Recalculate totals from entries and upsert status
  // ------------------------------------------------------------------
  const recalculateAndUpsert = useCallback(async (projectId, stageId, currentEntries) => {
    const closedPercent = currentEntries
      .filter(e => e.type === 'closure')
      .reduce((sum, e) => sum + e.percent, 0);

    const invoicedPercent = currentEntries
      .filter(e => e.type === 'invoice')
      .reduce((sum, e) => sum + e.percent, 0);

    // Clamp to 0–100 (should never exceed if UI validation works)
    const clampedClosed = Math.min(100, Math.max(0, closedPercent));
    const clampedInvoiced = Math.min(100, Math.max(0, invoicedPercent));

    return statusHook.upsertStatus(projectId, stageId, clampedClosed, clampedInvoiced);
  }, [statusHook]);

  // ------------------------------------------------------------------
  // Add a new entry
  // Validates remaining percent before insert.
  // ------------------------------------------------------------------
  const addEntry = useCallback(async ({ projectId, stageId, type, percent, entryDate, note }) => {
    try {
      await requireSession();

      // Validate: check remaining percent
      const currentOfType = entries
        .filter(e => e.type === type)
        .reduce((sum, e) => sum + e.percent, 0);
      const remaining = 100 - currentOfType;

      if (percent > remaining) {
        return {
          success: false,
          error: `Cannot add ${percent}%: only ${remaining}% remaining for ${type}.`,
        };
      }

      // Insert into ledger
      const { data, error } = await supabase
        .from('project_stage_entries')
        .insert([{
          project_id: projectId,
          stage_id: stageId,
          type,
          percent,
          entry_date: entryDate,
          note: note || null,
        }])
        .select()
        .single();

      if (error) throw error;

      // Map the new entry
      const newEntry = {
        id: data.id,
        projectId: data.project_id,
        stageId: data.stage_id,
        type: data.type,
        percent: data.percent,
        entryDate: data.entry_date,
        note: data.note || '',
        createdAt: data.created_at,
      };

      // Update local entries optimistically
      const updatedEntries = [...entries, newEntry].sort(
        (a, b) => a.entryDate.localeCompare(b.entryDate)
      );
      setEntries(updatedEntries);

      // Recalculate and upsert status
      await recalculateAndUpsert(projectId, stageId, updatedEntries);

      return { success: true, data: newEntry };
    } catch (err) {
      console.error('[PROJECT_STAGE_ENTRIES] Add error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'add', entity: 'stage entry' }) };
    }
  }, [entries, recalculateAndUpsert]);

  // ------------------------------------------------------------------
  // Delete an entry by ID, then recalculate
  // ------------------------------------------------------------------
  const deleteEntry = useCallback(async (entryId, projectId, stageId) => {
    try {
      await requireSession();

      const { error } = await supabase
        .from('project_stage_entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;

      // Remove from local state
      const updatedEntries = entries.filter(e => e.id !== entryId);
      setEntries(updatedEntries);

      // Recalculate and upsert status
      await recalculateAndUpsert(projectId, stageId, updatedEntries);

      return { success: true };
    } catch (err) {
      console.error('[PROJECT_STAGE_ENTRIES] Delete error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'stage entry' }) };
    }
  }, [entries, recalculateAndUpsert]);

  // ------------------------------------------------------------------
  // Computed: remaining percent for a given type
  // ------------------------------------------------------------------
  const getRemainingPercent = useCallback((type) => {
    const total = entries
      .filter(e => e.type === type)
      .reduce((sum, e) => sum + e.percent, 0);
    return Math.max(0, 100 - total);
  }, [entries]);

  // Clear entries (when collapsing / switching project)
  const clearEntries = useCallback(() => {
    setEntries([]);
    setLoadedKey(null);
  }, []);

  return {
    entries,
    loadingEntries,
    loadedKey,
    fetchEntries,
    addEntry,
    deleteEntry,
    getRemainingPercent,
    clearEntries,
  };
};

// ============================================================================
// HOOK: useProjectStageRealtime(onChangeCallback)
//
// Lightweight subscription hook for project_stage_status and
// project_stage_entries tables. Calls onChangeCallback (debounced 300ms)
// whenever any row changes, so the consuming page can re-fetch stage data.
// ============================================================================
export const useProjectStageRealtime = (onChangeCallback) => {
  const subscriptionsRef = useRef([]);
  const timeoutRef = useRef(null);
  const callbackRef = useRef(onChangeCallback);

  useEffect(() => { callbackRef.current = onChangeCallback; }, [onChangeCallback]);

  const debouncedCallback = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (callbackRef.current) callbackRef.current();
    }, 300);
  }, []);

  useEffect(() => {
    var statusSub = supabase
      .channel('stage-status-rt')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'project_stage_status' },
        function () { debouncedCallback(); }
      )
      .subscribe();

    var entriesSub = supabase
      .channel('stage-entries-rt')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'project_stage_entries' },
        function () { debouncedCallback(); }
      )
      .subscribe();

    subscriptionsRef.current = [statusSub, entriesSub];

    return function () {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      subscriptionsRef.current.forEach(function (sub) {
        supabase.removeChannel(sub);
      });
      subscriptionsRef.current = [];
    };
  }, [debouncedCallback]);
};
